import jwt_decode from "jwt-decode";

import { store } from '../app/store';
import { logger } from "../Util/Logger/ClientLogger";
import { appDebugLog } from '../Util/appDebugLog';
import { setCallRecordingStatusData, setAudioWaveform } from '../slices/callCentre';
import { getCallHistoryInitState } from '../slices/callHistory';
import TwilioVoiceService from './TwilioVoiceService';

const LogTitle = 'SaleforceService';
// ref : https://resources.docs.salesforce.com/246/latest/en-us/sfdc/pdf/api_cti.pdf

export default class SaleforceService {
  static callingObjectNavigation(phoneNumber: string, contactId: string, isOutbound: boolean) {

    appDebugLog(LogTitle, `objectSFNavigation phoneNumber ${phoneNumber} contactId: ${contactId} isOutbound: ${isOutbound}`);
    // @ts-ignore
    sforce?.opencti?.searchAndScreenPop({
      searchParams: phoneNumber,
      queryParams: '',
      defaultFieldValues: { rocketphone__ReferenceId__c: contactId, Phone: phoneNumber },
      // @ts-ignore
      callType: isOutbound ? sforce?.opencti?.CALL_TYPE.OUTBOUND : sforce?.opencti?.CALL_TYPE.INBOUND,
      deferred: false,
      callback: (response: any) => {
        if (response.success) {
          appDebugLog("API method call executed successfully! searchAndScreenPop returnValue:", response.returnValue);
        } else {
          console.error("Something went wrong!searchAndScreenPop Errors:", response.errors);
        }
      },
    });
  }

  static navigateToObject(objectId: string) {
    // @ts-ignore
    sforce.opencti.screenPop({
      // @ts-ignore
      type: sforce.opencti.SCREENPOP_TYPE.SOBJECT, //Review the arguments section.
      params: { recordId: objectId } //Depends on the SCREENPOP_TYPE. Review the arguments section.
    });
  }

  static publishObject(message: object, channel: string) {
    appDebugLog('published message:', {message, channel});
    // @ts-ignore
    sforce.opencti.publish({
      channelName: channel,
      message: message
    }); 
  }

  /*
      objectName : Opportunity, Case, Task
      referenceId: pass rp ref id 
  */
  static openObjectCreationModal(objectName: string, defaultFieldValues: any) {
    // @ts-ignore
    sforce.opencti.screenPop({
      // @ts-ignore
      type: sforce.opencti.SCREENPOP_TYPE.NEW_RECORD_MODAL,
      params: { entityName: objectName, defaultFieldValues }
    });
  }

  static setSoftPhoneVisibleIfNot() {
    const maxRetries = 3;
    const retryDelay = 1000;

    const attemptSetVisibility = (retryCount = 0) => {
      // @ts-ignore
      sforce?.opencti?.isSoftphonePanelVisible({
        callback: (response: any) => {
          if (response.success) {
            appDebugLog("Softphone panel visibility check successful:", response);
            logger.info('Softphone panel visibility check successful:', LogTitle, response);
            
            if (!response.returnValue.visible) {
              // @ts-ignore
              sforce?.opencti?.setSoftphonePanelVisibility({
                visible: true,
                callback: (setVisResponse: any) => {
                  if (setVisResponse.success) {
                    appDebugLog("Successfully set softphone panel visibility:", setVisResponse.returnValue);
                    logger.info('Successfully set softphone panel visibility:', LogTitle, setVisResponse.returnValue);
                  } else {
                    console.error("Failed to set softphone panel visibility:", setVisResponse.errors);
                    logger.error('Failed to set softphone panel visibility:', LogTitle, { error: setVisResponse.errors });

                    if (retryCount < maxRetries) {
                      appDebugLog(`Retrying set visibility attempt ${retryCount + 1} of ${maxRetries}...`);
                      setTimeout(() => attemptSetVisibility(retryCount + 1), retryDelay);
                    }
                  }
                },
              });
            }
          } else {
            console.error("Softphone panel visibility check successful failed:", response.errors);
            logger.error('Softphone panel visibility check successful failed:', LogTitle, { error: response.errors });

            if (retryCount < maxRetries) {
              appDebugLog(`Retrying visibility check attempt ${retryCount + 1} of ${maxRetries}...`);
              setTimeout(() => attemptSetVisibility(retryCount + 1), retryDelay);
            }
          }
        },
      });
    };

    attemptSetVisibility();
  }

  static getCallHistory() {
    const userAuthToken = store.getState()?.auth?.authUser?.AccessToken;
    const decoded: any = jwt_decode(userAuthToken);
    const packageVersion = decoded?.SFPackageVersion || '1.41';
    let apexClass = 'rocketphone.RPCallHelper';
    let methodName = 'getUserCallLogsFromTask';

    // if (packageVersion === '1.41') {
    //   apexClass = 'rocketphone.RPGetLoggedInUserInfo';
    //   methodName = 'getLoggedInUserCallLogs';
    // }

    // @ts-ignore
    sforce.opencti.runApex({
      apexClass,
      methodName,
      methodParams: '',
      callback: (response: any) => {
        let callRecords = [];

        if (response.success) {
          callRecords = JSON.parse(response.returnValue.runApex).map((result: any) => JSON.parse(result))
        } else {
          console.error('Error refreshing token:');
        }

        store.dispatch(getCallHistoryInitState({ listData: callRecords, loading: false }));
      }
    })
  };

  static getCallRecordDetailsByCallRecordId(callRecordId: string): Promise<any> {
    const userAuthToken = store.getState()?.auth?.authUser?.AccessToken;
    const decoded: any = jwt_decode(userAuthToken);
    const packageVersion = decoded?.SFPackageVersion || '1.41';
    let apexClass = 'rocketphone.RPCallHelper';
    let methodName = 'getCallLogFromTaskUsingCallId';

    // if (packageVersion === '1.41') {
    //   apexClass = 'rocketphone.RPGetLoggedInUserInfo';
    //   methodName = 'getCallLogsUsingCallId';
    // }

    return new Promise<any>((resolve, reject) => {
      // @ts-ignore
      sforce.opencti.runApex({
        apexClass,
        methodName,
        methodParams: `callId=${callRecordId}`,
        callback: (response: any) => {
          let objects = [];
          if (response.success) {
            objects = JSON.parse(response.returnValue.runApex).map((result: any) => JSON.parse(result));
            resolve(objects?.[0] || {});
          } else {
            console.error('Error on featching call object by callRecordId');
            reject(new Error('Error on featching call object by callRecordId'));
          }
        }
      });
    });
  }

  static getContactSearchByPhone(phoneNumber: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      // @ts-ignore
      sforce.opencti.runApex({
        apexClass: 'rocketphone.RPCallHelper',
        methodName: 'getObjectsByPhone',
        methodParams: `phoneNumber=${phoneNumber}`,
        callback: (response: any) => {
          let objects = [];
          if (response.success) {
            objects = JSON.parse(response.returnValue.runApex).map((result: any) => JSON.parse(result));
            resolve(objects);
          } else {
            console.error('Error on featching object by phone');
            reject(new Error('Error on featching object by phone'));
          }
        }
      });
    });
  }

  static getTasksAndBookmarksByLookupId(lookupRecordId: any, callRecordId: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      // @ts-ignore
      sforce.opencti.runApex({
        apexClass: 'rocketphone.RPCallHelper',
        methodName: 'getTasksAndBookmarksByLookupId',
        methodParams: `lookupId=${lookupRecordId}&callRecordId=${callRecordId}`,
        callback: (response: any) => {
          if (response.success) {
            appDebugLog('getTasksAndBookmarksByLookupId response returnValue: ', response?.returnValue);
          } else {
            console.error('Error on calling getTasksAndBookmarksByLookupId');
          }
        }
      });
    });
  }

  static getObjectDetails(type: string, objectId: string, ctdNumber: string): Promise<any> {
    const userAuthToken = store.getState()?.auth?.authUser?.AccessToken;
    const decoded: any = jwt_decode(userAuthToken);
    const packageVersion = decoded?.SFPackageVersion || '1.41';
    let apexClass = 'rocketphone.RPGetLoggedInUserInfo';
    let methodName = 'getObjectsById';
    let methodParams = `remoteId=${objectId}&typeOfObject=${type}`;

    const packageVersionNum = parseFloat(packageVersion);
    const targetVersionNum = 1.67;

    if (packageVersionNum > targetVersionNum) {
      apexClass = 'rocketphone.RPCallHelper';
      methodName = 'getObjectsByIdAndPhone';
      methodParams = `remoteId=${objectId}&typeOfObject=${type}&phoneNumber=${ctdNumber}`;
    }

    return new Promise<any>((resolve, reject) => {
      // @ts-ignore
      sforce.opencti.runApex({
        apexClass,
        methodName,
        methodParams,
        callback: (response: any) => {
          if (response.success) {
            const objectDetails = JSON.parse(response.returnValue.runApex);
            resolve(objectDetails);
          } else {
            console.error('Error refreshing token:');
            reject(new Error('Error on featching object'));
          }
        }
      });
    });
  }

  static publishAudioWaveformData(audioData: {
    microphoneLevel: number;
    phoneLevel: number;
    combinedLevel: number;
    frequencyData: number[] | null;
    isActive: boolean;
  }) {
    // @ts-ignore
    sforce.opencti.publish({
      channelName: "rocketphone__RPISTOLWC__c",
      message: {
        type: 'AUDIO_WAVEFORM_DATA',
        audioData
      }
    });
  }

  static processLWCMessage(message: any){
    // GET_BOOKMARKS // CALL_ID
    // GET_TRANSCRIPT // CALL_ID
    // GET_INPROGRESS_CALL_DETAILS
    // REGISTER
    // UNREGISTER
    const callDetails = TwilioVoiceService.getCallDetailsFromConnection(TwilioVoiceService.callConnection);
    const callAgent = store.getState()?.auth?.user;
    // This is connected object
    const callingContact = store.getState()?.callingContact?.data;
    appDebugLog("Main Conmponent", "callingContact : ", {callingContact});
    if (message?.type === 'GET_INPROGRESS_CALL_DETAILS') {
      appDebugLog("Main Conmponent", "Published when received GET_INPROGRESS_CALL_DETAILS msg, msgObjcallDetails: ", {callDetails});
      SaleforceService.publishObject({
        type: 'GET_INPROGRESS_CALL_DETAILS', 
        callDetails: callDetails,
        assignedUser: callAgent,
        sfcontact: callingContact
      }, "rocketphone__RPISTOLWC__c");
      appDebugLog(message?.type);
    } else if (message?.type === 'GET_INPROGRESS_CALL_BOOKMARKS') {
      let callRecordId = message?.callId;
      appDebugLog("Main Conmponent", "callRecordId : ", {callRecordId});
      let captures = store.getState()?.callTranscript?.capturesTranscriptMap;
      appDebugLog("Main Conmponent", "Published when received GET_INPROGRESS_CALL_BOOKMARKS msg, msgObjcallDetails: ", {captures});
      let InProgressCallCaptures = captures[callRecordId];
      appDebugLog("Main Conmponent", "InProgressCallCaptures : ", {InProgressCallCaptures});
      SaleforceService.publishObject({
        type: 'GET_INPROGRESS_CALL_BOOKMARKS', 
        callDetails: callDetails,
        bookmarkCaptures: InProgressCallCaptures
      }, "rocketphone__RPISTOLWC__c");
    }
    else if (message?.type === 'GET_INPROGRESS_CALL_SFCONTACT') {
      appDebugLog("Salesforce Service", "GET_INPROGRESS_CALL_SFCONTACT received msg : ", {message});
      SaleforceService.publishObject({
        type: 'GET_INPROGRESS_CALL_SFCONTACT', 
        callDetails: callDetails,
        sfcontact: callingContact
      }, "rocketphone__RPISTOLWC__c");
    }
    else if (message?.type === 'GET_INPROGRESS_PCI_QUESTIONS') {
      let currentActiveCallRecordId = message?.currentCallRecordId;
      TwilioVoiceService.pauseRecording(currentActiveCallRecordId, true);
      store.dispatch(setCallRecordingStatusData({callRecordId: currentActiveCallRecordId, status: false }));
    }
    else if(message?.type === 'ACTIVATE_AUDIO_WAVEFORM') {
      store.dispatch(setAudioWaveform(message?.active));
    }
  }
}